// ./src/components/features/Dashboard/DashboardRecentActivity/DashboardRecentActivity.tsx
// Dashboard section showing recent expenses, revenues, and travels in a unified list.
// Uses DashboardSection + DashboardItemTile for consistent tile layout.

import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { EXPENSE_TYPES, getDistanceUnitOption, getVolumeUnitOption } from "@/config";

import {
  formatCurrency,
  formatDistance,
} from "@/utils/formatHelpers";
import { formatDateTime } from "@/utils/dateHelpers";

import { Expense } from "@/utils/api/ExpenseService";
import { Travel } from "@/utils/api/TravelService";
import { Car } from "@/utils/api/CarService";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { DashboardSection } from "./DashboardSection";
import { DashboardItemTile, TileValueType } from "./DashboardItemTile";

// ============================================
// Types
// ============================================

export type ActivityType =
  | "expense"
  | "refuel"
  | "checkpoint"
  | "trip"
  | "travel"
  | "revenue";

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  subtitle?: string;
  description?: string;
  amount?: number;
  currency?: string;
  distance?: number;
  odometer?: number;
  volume?: number;
  isFullTank?: boolean;
  fuelGrade?: string;
  carId?: string;
  carLabel?: string;
  timestamp: string;
  iconName: string;
  color: string;
}

export interface DashboardRecentActivityProps {
  /** Array of expenses */
  expenses?: Expense[];

  /** Array of travels */
  travels?: Travel[];

  /** Map of cars by ID for labels */
  carsMap?: Map<string, Car>;

  /** Loading state */
  loading?: boolean;

  /** Maximum items to display */
  maxItems?: number;

  /** Handler for clicking an activity item */
  onActivityClick?: (item: ActivityItem) => void;

  /** Handler for "View All" button */
  onViewAll?: () => void;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const ACTIVITY_CONFIG: Record<
  ActivityType,
  { iconName: string; color: string }
> = {
  expense: { iconName: "Receipt", color: "brand" },
  refuel: { iconName: "GasStation", color: "cyan" },
  checkpoint: { iconName: "MapPin", color: "green" },
  trip: { iconName: "Route", color: "grape" },
  travel: { iconName: "Route", color: "grape" },
  revenue: { iconName: "CurrencyDollar", color: "teal" },
};

// ============================================
// Expense/Travel → ActivityItem Mappers
// ============================================

const getActivityTypeFromExpenseType = (expenseType?: number): ActivityType => {
  switch (expenseType) {
    case EXPENSE_TYPES.REFUEL:
      return "refuel";
    case EXPENSE_TYPES.CHECKPOINT:
      return "checkpoint";
    case EXPENSE_TYPES.TRAVEL_POINT:
      return "trip";
    case EXPENSE_TYPES.REVENUE:
      return "revenue";
    case EXPENSE_TYPES.EXPENSE:
    default:
      return "expense";
  }
};

const mapExpenseToActivity = (
  expense: Expense,
  carLabel?: string,
  expenseKindName?: string,
  revenueKindName?: string,
  t?: (key: string) => string
): ActivityItem => {
  const activityType = getActivityTypeFromExpenseType(expense.expenseType);
  const config = ACTIVITY_CONFIG[activityType];

  let title = t ? t("expenseTypes.expense") : "Expense";
  let subtitle: string | undefined;

  switch (activityType) {
    case "refuel":
      title = t ? t("expenseTypes.refuel") : "Refuel";
      if (expense.whereDone || expense.location) {
        subtitle = expense.whereDone || expense.location;
      }
      break;
    case "checkpoint":
      title = t ? t("expenseTypes.checkpoint") : "Checkpoint";
      subtitle = expense.comments || expense.location || undefined;
      break;
    case "trip":
      title = t ? t("expenseTypes.trip") : "Trip";
      subtitle = expense.comments || undefined;
      break;
    case "revenue":
      title =
        revenueKindName ||
        expense.shortNote ||
        (t ? t("expenseTypes.revenue") : "Revenue");
      subtitle =
        expense.shortNote && revenueKindName
          ? expense.shortNote
          : expense.whereDone || expense.location || undefined;
      break;
    case "expense":
    default:
      title =
        expenseKindName ||
        expense.shortNote ||
        (t ? t("expenseTypes.expense") : "Expense");
      subtitle =
        expense.shortNote && expenseKindName
          ? expense.shortNote
          : expense.comments || undefined;
      break;
  }

  return {
    id: expense.id || "",
    type: activityType,
    title,
    subtitle,
    description: expense.comments || undefined,
    carId: expense.carId,
    carLabel,
    odometer: expense.odometer,
    amount: expense.totalPriceInHc || expense.totalPrice,
    currency: expense.homeCurrency || expense.paidInCurrency || "USD",
    volume: expense.refuelVolume,
    isFullTank: expense.isFullTank,
    fuelGrade: expense.fuelGrade,
    timestamp: expense.whenDone || expense.createdAt || "",
    iconName: config.iconName,
    color: config.color,
  };
};

const mapTravelToActivity = (
  travel: Travel,
  carLabel?: string,
  t?: (key: string) => string
): ActivityItem => {
  const startMileage = (travel as any).startMileage || 0;
  const endMileage = (travel as any).endMileage || 0;
  const distance = Math.max(0, endMileage - startMileage);

  const config = ACTIVITY_CONFIG.travel;

  return {
    id: travel.id || "",
    type: "travel",
    title:
      (travel as any).description || (t ? t("expenseTypes.travel") : "Travel"),
    subtitle: (travel as any).comments || undefined,
    carId: (travel as any).carId,
    carLabel,
    odometer: endMileage,
    distance,
    timestamp: (travel as any).firstDttm || (travel as any).createdAt || "",
    iconName: config.iconName,
    color: config.color,
  };
};

// ============================================
// Tile Mapping Helpers
// ============================================

/**
 * Build subtitle line: "Car Name · Relative Time · Odometer"
 */
const buildTileSubtitle = (
  item: ActivityItem,
  distanceLabel: string
): string => {
  const parts: string[] = [];

  if (item.carLabel) {
    parts.push(item.carLabel);
  }

  if (item.timestamp) {
    parts.push(formatDateTime(item.timestamp, "relative"));
  }

  if (item.odometer != null && item.odometer > 0) {
    parts.push(`${item.odometer.toLocaleString()} ${distanceLabel}`);
  }

  return parts.join(" · ");
};

/**
 * Build description line.
 * For refuels: combines location with "15.5 L • Full tank • Premium"
 * For others: uses subtitle (location/notes) as description.
 */
const buildDescription = (
  item: ActivityItem,
  volumeLabel: string,
  t: (key: string) => string
): string | undefined => {
  if (item.type === "refuel") {
    const parts: string[] = [];

    if (item.volume && item.volume > 0) {
      parts.push(`${item.volume} ${volumeLabel}`);
    }

    if (item.isFullTank) {
      parts.push(t("refuel.fullTank"));
    }

    if (item.fuelGrade) {
      parts.push(item.fuelGrade);
    }

    if (parts.length > 0) {
      const refuelInfo = parts.join(" • ");
      return item.subtitle ? `${item.subtitle} — ${refuelInfo}` : refuelInfo;
    }

    return item.subtitle;
  }

  // For non-refuel types, show subtitle (location/notes) as description
  return item.subtitle;
};

/**
 * Format value text for the right side of the tile.
 */
const buildValueText = (
  item: ActivityItem,
  distanceUnit: string
): string | undefined => {
  // Travel: show distance
  if (item.type === "travel" && item.distance != null && item.distance > 0) {
    return formatDistance(item.distance, distanceUnit, 0, true);
  }

  // Expense/Revenue: show amount
  if (item.amount && item.amount > 0 && item.currency) {
    const formatted = formatCurrency(item.amount, item.currency);
    return item.type === "revenue" ? `+${formatted}` : formatted;
  }

  return undefined;
};

/**
 * Get value type for styling.
 */
const getValueType = (item: ActivityItem): TileValueType => {
  if (item.type === "travel") return "distance";
  if (item.type === "revenue") return "revenue";
  return "expense";
};

// ============================================
// Main Component
// ============================================

/**
 * Displays recent activity (expenses, revenues, and travels) in a unified list.
 * Items are merged and sorted by timestamp descending.
 */
export const DashboardRecentActivity: React.FC<DashboardRecentActivityProps> = ({
  expenses = [],
  travels = [],
  carsMap,
  loading = false,
  maxItems = 10,
  onActivityClick,
  onViewAll,
}) => {
  const { t } = useTranslation();
  const { distanceUnit, volumeUnit } = useUserProfileContext();
  const { getExpenseKindNameById, getRevenueKindNameById } =
    useAppEssentialsContext();

  // Resolve unit labels once
  const distanceLabel = t(`common:${getDistanceUnitOption(distanceUnit)?.labelKey}`);
  const volumeLabel = t(`common:${getVolumeUnitOption(volumeUnit)?.labelKey}`);

  // Merge and sort activities
  const activities = useMemo(() => {
    const getCarLabel = (carId?: string) => {
      if (!carId || !carsMap) return undefined;
      const car = carsMap.get(carId);
      return car?.label || car?.vehicleMake?.makeName || undefined;
    };

    const expenseActivities = expenses.map((expense) => {
      const isRevenue = expense.expenseType === EXPENSE_TYPES.REVENUE;
      const expenseKindName = isRevenue
        ? undefined
        : getExpenseKindNameById(expense.kindId);
      const revenueKindName = isRevenue
        ? getRevenueKindNameById(expense.kindId)
        : undefined;

      return mapExpenseToActivity(
        expense,
        getCarLabel(expense.carId),
        expenseKindName,
        revenueKindName,
        t
      );
    });

    const travelActivities = travels.map((travel) =>
      mapTravelToActivity(travel, getCarLabel((travel as any).carId), t)
    );

    const merged = [...expenseActivities, ...travelActivities].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });

    return maxItems > 0 ? merged.slice(0, maxItems) : merged;
  }, [
    expenses,
    travels,
    carsMap,
    maxItems,
    getExpenseKindNameById,
    getRevenueKindNameById,
    t,
  ]);

  // Click handler factory
  const handleItemClick = useCallback(
    (item: ActivityItem) => () => {
      onActivityClick?.(item);
    },
    [onActivityClick]
  );

  const totalCount = expenses.length + travels.length;

  return (
    <DashboardSection
      title={t("dashboard.recentActivity")}
      headerAction={
        onViewAll
          ? {
            label: t("dashboard.viewAllActivity"),
            onClick: onViewAll,
          }
          : undefined
      }
      loading={loading}
      totalItems={totalCount}
      initialDisplayCount={maxItems}
      emptyState={{
        title: t("dashboard.noRecentActivity"),
        iconName: "History",
      }}
    >
      {activities.map((item) => (
        <DashboardItemTile
          key={`${item.type}-${item.id}`}
          title={item.title}
          iconName={item.iconName}
          iconColor={item.color}
          subtitle={buildTileSubtitle(item, distanceLabel)}
          description={buildDescription(item, volumeLabel, t)}
          valueText={buildValueText(item, distanceUnit)}
          valueType={getValueType(item)}
          onClick={onActivityClick ? handleItemClick(item) : undefined}
        />
      ))}
    </DashboardSection>
  );
};